import { LinkButton, toast } from '@pmi/dsm-react';
import { useEffect, useRef, useState } from 'react';
import { SearchIcon } from '@pmi/dsm-react/dist/components/icons/fa/24';
import styles from './logSearchField.module.scss';
import logSearchHelper from './helper';
import { SearchItem, SearchItemMap, SearchLogColumn } from './models';

export interface SearchFieldProps {
  onSearch: (data: { [key: string]: string } | null) => void;
  columns?: SearchLogColumn[];
  disabled?: boolean;
  mapColumns?: SearchItemMap[];
}

export default function LogSearchField({
  onSearch,
  columns = [],
  disabled = false,
  mapColumns = [],
}: SearchFieldProps) {
  const [searchString, setSearchString] = useState('');
  const [showColumns, setShowColumns] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState('');
  const [searchData, setSearchData] = useState<SearchItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isNavigating, setIsNavigating] = useState(false);
  const [columnLi, setColumnLi] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [placeHolder, setPlaceHolder] = useState('Search logs');
  const inputRef = useRef<HTMLInputElement>(null);
  const updateSearchData = (): SearchItem[] => {
    let temp: SearchItem[] = [];
    if (searchString.length > 0) {
      temp = logSearchHelper.strToSearchData(searchString, columns);
      const filtered = temp.filter((item) => item.value.length === 0);
      if (filtered.length > 0) {
        setInvalid(true);
        setError(`Invalid search string.`);
      }
    }
    setSearchData(temp);
    return temp;
  };
  const onColumnSelected = (column: SearchLogColumn) => {
    const lastChar = searchString.trim().slice(-1);
    let str = `${
      searchString.length > 0
        ? `${searchString.trim()}${lastChar === ',' ? ' ' : ', '} `
        : ''
    }${column.label}: `;
    setShowColumns(false);
    setIsNavigating(false);
    if (searchString.trim().indexOf(':') < 0) {
      str = `${column.label}: ${searchString.trim()}`;
    }
    setSearchString(str);
    inputRef.current.focus();
    inputRef.current.setSelectionRange(
      inputRef.current.value.length,
      inputRef.current.value.length,
    );
  };
  const submitSearchData = () => {
    if (submitted) {
      return;
    }
    if (invalid) {
      toast('Log Search Error', 'danger', error);
      return;
    }
    if (searchData.length === 0) {
      onSearch(null);
      return;
    }
    setSearchString(searchString.replace(/\s{2,}/g, ' ').trim());
    setSubmitted(true);
    if (searchData) {
      onSearch(logSearchHelper.toSearchFormat(searchData));
    }
  };
  const onKeyup = (e: any | Event) => {
    const target = e?.target;
    updateSearchData();
    logSearchHelper.storeSearch(searchData);
    setInvalid(false);
    if (e.keyCode === 27) {
      setShowColumns(false);
      setCurrentIndex(0);
      return;
    }
    if (e.keyCode === 13) {
      if (searchData.length === 0) {
        setInvalid(true);
        setError('Invalid search string');
        return;
      }
      if (!isNavigating || currentIndex < 0) {
        setShowColumns(false);
        setCurrentIndex(0);
        submitSearchData();
      } else {
        try {
          if (currentIndex >= 0) {
            columnLi[currentIndex].querySelector('a')?.click();
          }
          setTimeout(() => {
            setSearchString(target.value);
            updateSearchData();
          }, 10);
          target.setSelectionRange(target.value.length, target.value.length);
        } catch (er) {
          console.log(er);
        }
      }
    }
    if (showColumns) {
      const maxIndex =
        searchData.length > 0
          ? columns.length - searchData.length
          : columns.length;
      const cursorLast = () =>
        target.setSelectionRange(target.value.length, target.value.length);
      if (e.keyCode === 40) {
        if (currentIndex < maxIndex - 1) {
          setCurrentIndex(currentIndex + 1);
          cursorLast();
        }
        if (currentIndex >= maxIndex) {
          setCurrentIndex(maxIndex - 1);
          cursorLast();
        }
      }
      if (e.keyCode === 38) {
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
          cursorLast();
        }
        if (currentIndex < 0) {
          setCurrentIndex(0);
          cursorLast();
        }
      }
      return;
    }
    if (e.keyCode === 40 || e.keyCode === 188 || e.keyCode === 32) {
      setCurrentIndex(0);
      setShowColumns(true);
      return;
    }
    if (e.keyCode === 40 || e.keyCode === 188 || e.keyCode === 32) {
      setCurrentIndex(-1);
      setShowColumns(true);
      return;
    }
    setShowColumns(false);
    setIsNavigating(false);
  };
  const onKeyDown = () => logSearchHelper.storeSearch(searchData);
  const onChange = (e: any | Event) => {
    const { value } = e.target;
    setSearchString(value);
    updateSearchData();
    logSearchHelper.storeSearch(searchData);
    if (value.length === 0) {
      setShowColumns(true);
    }
  };
  const hideColumnSelection = (timeout = 300) => {
    setCurrentIndex(-1);
    setTimeout(() => setShowColumns(false), timeout);
  };
  const isColumnSelected = (column: SearchLogColumn) =>
    searchData.find((col) => column.value === col.column);
  useEffect(() => {
    if (mapColumns.length > 0) {
      const storedString = logSearchHelper.getStoredSearch();
      if (!storedString) {
        return () => {};
      }
      try {
        const parsedData: SearchItem[] = JSON.parse(storedString);
        if (parsedData.length > 0) {
          const str: string[] = [];
          const data: SearchItem[] = [];
          mapColumns.map((r: SearchItemMap) => {
            const column = columns.find((c) => c.value === r.with);
            if (column) {
              const found: SearchItem = parsedData.find(
                (p: SearchItem) => p.column === r.column,
              );
              if (found) {
                str.push(`${column.label}: ${found.value}`);
                data.push({ column: r.with, value: found.value });
              }
            }
            return r;
          });
          setSearchString(str.join(', ') ?? '');
          setSearchData(data);
          onSearch(logSearchHelper.toSearchFormat(data));
        }
      } catch (ex) {
        console.log(ex);
      }
    }
    inputRef.current = document.getElementById(
      'logSearchTextBox',
    ) as HTMLInputElement;
    return () => {};
  }, []);
  useEffect(() => {
    if (searchString.trim().length === 0) {
      setCurrentIndex(-1);
    }
    setSubmitted(false);
    return () => {};
  }, [searchString]);
  useEffect(() => {
    updateSearchData();
    if (showColumns) {
      const li = document.querySelectorAll('#ulColumnSelection li');
      setIsNavigating(true);
      setCurrentIndex(-1);
      setColumnLi(li);
      setTimeout(() => {
        if (li) {
          li?.forEach((el: Element) =>
            el.querySelector('a').classList.remove('active'),
          );
        }
      }, 10);
    }
    return () => {};
  }, [showColumns]);
  useEffect(() => {
    if (searchData.length === columns.length) {
      setShowColumns(false);
    }
    if (showColumns && columnLi && columnLi.length > 0 && currentIndex >= 0) {
      columnLi?.forEach((el: Element) =>
        el.querySelector('a').classList.remove('active'),
      );
      columnLi[currentIndex]?.querySelector('a').classList.add('active');
    }
    return () => {};
  }, [currentIndex, searchString, searchData]);
  return (
    <div className="d-block my-3">
      <div
        className={`${styles.searchFieldContainer} ${
          invalid ? styles.isInvalidSearch : ''
        } ${
          showColumns && searchData.length < columns.length
            ? `${styles.columnShown} shadow`
            : ''
        } `}
      >
        <div
          className={`d-flex h-100 align-content-center align-items-center ${styles.searchField}`}
        >
          <LinkButton
            icon={SearchIcon}
            titleText=""
            className={`${styles.searchButton} iconSearch`}
            onClick={disabled ? () => null : submitSearchData}
          />
          <input
            id="logSearchTextBox"
            type="text"
            className={`form-control shadow-none ${disabled ? 'disabled' : ''}`}
            placeholder={placeHolder}
            disabled={disabled}
            autoComplete="off"
            onChange={onChange}
            value={searchString}
            onFocus={() => {
              if (!disabled) {
                setShowColumns(true);
                setCurrentIndex(0);
              }
              if (searchData.length === 0) {
                setPlaceHolder('Select field to search');
              }
            }}
            onKeyUp={onKeyup}
            onKeyDown={onKeyDown}
            onKeyPress={(e: Event | any) => {
              if (searchString.length === 0) {
                e.preventDefault();
                setShowColumns(true);
              }
            }}
            onBlur={() => hideColumnSelection()}
            onDoubleClick={() => {
              if (!disabled) {
                setShowColumns(true);
              }
            }}
          />
        </div>
        {!disabled && showColumns && searchData.length < columns.length && (
          <div
            className={`${styles.searchColumnSelector} bg-white round shadow`}
          >
            {columns.length > 0 && (
              <ul className="m-0" id="ulColumnSelection">
                {columns.map((column: SearchLogColumn) => {
                  if (!isColumnSelected(column)) {
                    return (
                      <li className="d-block" key={column.value}>
                        <LinkButton
                          titleText={column.label}
                          variant="light"
                          onClick={() => onColumnSelected(column)}
                          className={`${styles.btnSelection}`}
                        />
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
